import { useState } from 'react';
import { Link } from 'react-router-dom';
import { HomiLogo } from '@/components/HomiLogo';
import {
  Users, Bell, PhoneCall, CalendarCheck, LineChart, Sparkles,
  ChevronDown, ArrowRight, Menu, X,
} from 'lucide-react';

// SVG mockup of the HomiHQ Today dashboard, drawn on-brand (sage + gold + ivory).
// Purely decorative — no external image asset needed.
function DashboardMockup() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        maxWidth: 940,
        margin: '4rem auto 0',
        borderRadius: 6,
        overflow: 'hidden',
        boxShadow: '0 30px 60px -30px rgba(59, 70, 52, 0.28), 0 12px 24px -12px rgba(59, 70, 52, 0.15)',
        border: '1px solid #E4D9C6',
        background: '#FBF6EC',
      }}
    >
      <svg viewBox="0 0 1200 700" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%', height: 'auto' }}>
        <defs>
          <linearGradient id="hq-page" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FBF6EC" />
            <stop offset="100%" stopColor="#F4EADE" />
          </linearGradient>
          <linearGradient id="hq-sidebar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4F5D44" />
            <stop offset="100%" stopColor="#3B4634" />
          </linearGradient>
        </defs>

        {/* Page background */}
        <rect x="0" y="0" width="1200" height="700" fill="url(#hq-page)" />

        {/* Sidebar */}
        <rect x="0" y="0" width="220" height="700" fill="url(#hq-sidebar)" />

        {/* Sidebar logo */}
        <circle cx="42" cy="42" r="14" fill="none" stroke="#D4AF37" strokeWidth="1.2" />
        <text x="42" y="47" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="16" fill="#F4EADE" textAnchor="middle">H</text>
        <text x="72" y="42" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="16" fill="#F4EADE" letterSpacing="3.5">HOMIHQ</text>
        <text x="72" y="58" fontFamily="Inter, sans-serif" fontSize="8" fill="#D4AF37" letterSpacing="2.2">POWERED BY HIRED</text>

        {/* Sidebar nav items */}
        {[
          ['Today', true],
          ['AI Employee'],
          ['Call Command'],
          ['Lead Shield'],
          ['Deal Desk'],
          ['Clients'],
          ['Listing Legend'],
          ['Contract Clerk'],
          ['Neighborhood Intel'],
          ['Pricing'],
        ].map(([label, active], i) => (
          <g key={label}>
            {active && <rect x="0" y={110 + i * 44 - 12} width="4" height="28" fill="#D4AF37" />}
            <circle cx="34" cy={110 + i * 44} r="3" fill={active ? '#D4AF37' : '#7A8770'} />
            <text
              x="52"
              y={114 + i * 44}
              fontFamily="Inter, sans-serif"
              fontSize="11"
              letterSpacing="1.2"
              fill={active ? '#F4EADE' : '#B8B2A2'}
            >
              {label.toUpperCase()}
            </text>
          </g>
        ))}

        {/* Main content — top bar */}
        <text x="256" y="52" fontFamily="Inter, sans-serif" fontSize="10" letterSpacing="3" fill="#C9A227">TODAY · TUESDAY, FEB 24</text>
        <text x="256" y="92" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="34" fill="#3B4634">Good morning, Kassie.</text>
        <rect x="256" y="104" width="42" height="1" fill="#D4AF37" />

        {/* Metric tiles */}
        {[
          { x: 256, label: 'NEW LEADS', value: '12', delta: '+3 today' },
          { x: 464, label: 'FOLLOW-UPS DUE', value: '7', delta: 'On track' },
          { x: 672, label: 'DEALS IN MOTION', value: '9', delta: '$4.2M pipeline' },
          { x: 880, label: 'APPOINTMENTS', value: '4', delta: 'Next: 11:30 AM' },
        ].map((m) => (
          <g key={m.label}>
            <rect x={m.x} y="140" width="188" height="102" rx="3" fill="#FBF6EC" stroke="#E4D9C6" />
            <text x={m.x + 18} y="166" fontFamily="Inter, sans-serif" fontSize="9" letterSpacing="1.8" fill="#7A8770">{m.label}</text>
            <text x={m.x + 18} y="210" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="40" fill="#4F5D44">{m.value}</text>
            <text x={m.x + 18} y="232" fontFamily="Inter, sans-serif" fontSize="10" fill="#C9A227">{m.delta}</text>
          </g>
        ))}

        {/* Priorities panel */}
        <rect x="256" y="272" width="602" height="380" rx="3" fill="#FBF6EC" stroke="#E4D9C6" />
        <text x="278" y="304" fontFamily="Inter, sans-serif" fontSize="10" letterSpacing="2.5" fill="#C9A227">TODAY&apos;S PRIORITIES</text>
        <text x="278" y="342" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="22" fill="#3B4634">What needs your attention</text>
        <line x1="278" y1="360" x2="836" y2="360" stroke="#E4D9C6" />

        {[
          { title: 'Call the Whitfield family back', sub: 'Warm lead · 3.2M budget · Requested this morning', pill: 'CALL' },
          { title: 'Send follow-up to 415 Oak Grove buyers', sub: 'Follow-up · Automated draft ready · Awaiting review', pill: 'REVIEW' },
          { title: 'Prep listing copy — 2871 Ridgemont', sub: 'Listing · Photos received · Draft in 2 min with AI', pill: 'DRAFT' },
          { title: 'Confirm 11:30 showing — Marisol Chen', sub: 'Appointment · Property confirmed · Send reminder', pill: 'CONFIRM' },
        ].map((row, i) => {
          const y = 386 + i * 60;
          return (
            <g key={row.title}>
              <circle cx="292" cy={y + 10} r="6" fill="none" stroke="#D4AF37" strokeWidth="1.3" />
              <text x="316" y={y + 8} fontFamily="Inter, sans-serif" fontSize="13" fill="#3B4634" fontWeight="600">{row.title}</text>
              <text x="316" y={y + 28} fontFamily="Inter, sans-serif" fontSize="11" fill="#7A8770">{row.sub}</text>
              <rect x="748" y={y - 4} width="86" height="22" rx="2" fill="#F4EADE" stroke="#D4AF37" />
              <text x="791" y={y + 11} fontFamily="Inter, sans-serif" fontSize="9" letterSpacing="1.8" fill="#C9A227" textAnchor="middle">{row.pill}</text>
            </g>
          );
        })}

        {/* Right column — AI card + Pipeline card */}
        <rect x="880" y="272" width="288" height="182" rx="3" fill="#4F5D44" />
        <text x="900" y="302" fontFamily="Inter, sans-serif" fontSize="9" letterSpacing="2.4" fill="#D4AF37">AI EMPLOYEE</text>
        <text x="900" y="336" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="20" fill="#F4EADE">3 drafts ready for review</text>
        <line x1="900" y1="352" x2="1140" y2="352" stroke="rgba(212,175,55,0.35)" />
        <text x="900" y="376" fontFamily="Inter, sans-serif" fontSize="11" fill="#EFE4D2">· Follow-up to Whitfield family</text>
        <text x="900" y="396" fontFamily="Inter, sans-serif" fontSize="11" fill="#EFE4D2">· MLS copy — 2871 Ridgemont</text>
        <text x="900" y="416" fontFamily="Inter, sans-serif" fontSize="11" fill="#EFE4D2">· Neighborhood brief — Ridgemont</text>
        <rect x="900" y="428" width="112" height="18" rx="2" fill="#D4AF37" />
        <text x="956" y="441" fontFamily="Inter, sans-serif" fontSize="9" letterSpacing="2.2" fill="#FFFFFF" textAnchor="middle">REVIEW ALL</text>

        <rect x="880" y="470" width="288" height="182" rx="3" fill="#FBF6EC" stroke="#E4D9C6" />
        <text x="900" y="500" fontFamily="Inter, sans-serif" fontSize="9" letterSpacing="2.4" fill="#C9A227">PIPELINE THIS WEEK</text>
        <text x="900" y="534" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="26" fill="#3B4634">$4.2M</text>
        <text x="900" y="552" fontFamily="Inter, sans-serif" fontSize="10" fill="#7A8770">9 deals across 3 stages</text>
        {/* Sparkline */}
        <polyline
          points="900,610 940,596 980,604 1020,582 1060,590 1100,568 1140,556"
          fill="none" stroke="#4F5D44" strokeWidth="1.4"
        />
        {[900,940,980,1020,1060,1100,1140].map((cx, i) => (
          <circle key={cx} cx={cx} cy={[610,596,604,582,590,568,556][i]} r="2.2" fill="#D4AF37" />
        ))}
      </svg>
    </div>
  );
}

// Landing-page-scoped palette — HomiHQ house palette: sage, gold, ivory.
const C = {
  bg: '#F4EADE',        // ivory
  bgAlt: '#EFE4D2',     // warmer ivory for alternating sections
  bgCard: '#FBF6EC',    // card ivory
  ink: '#2A2A28',       // near-black body ink
  inkSoft: '#5A5A55',   // muted body ink
  line: '#E4D9C6',      // hairline
  green: '#4F5D44',     // sage — primary
  greenDark: '#3B4634', // sage-deep hover
  gold: '#D4AF37',      // matte gold — accent
  goldSoft: '#C9A227',
};

const NAV_ITEMS = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', to: '/pricing' },
];

const FEATURES = [
  { icon: Users, title: 'Lead Command Center', body: 'Keep every prospect, conversation, and next step organized in one place.' },
  { icon: Bell, title: 'Smart Follow-Ups', body: 'Stay in front of leads with timely reminders and automated outreach.' },
  { icon: PhoneCall, title: 'Calling Workspace', body: 'Plan and manage prospecting calls without losing track of conversations.' },
  { icon: CalendarCheck, title: 'Appointments & Tasks', body: 'See what is due, what is scheduled, and what needs attention next.' },
  { icon: LineChart, title: 'Pipeline Visibility', body: 'Know where every opportunity stands and what will move it forward.' },
  { icon: Sparkles, title: 'AI-Powered Support', body: 'Use intelligent assistance to reduce repetitive work and keep business moving.' },
];

const STEPS = [
  { n: '01', title: 'Add your leads', body: 'Import or manually add contacts to build your pipeline.' },
  { n: '02', title: 'Let HomiHQ guide the follow-up', body: 'Automated reminders and smart suggestions keep you on track.' },
  { n: '03', title: 'Keep every deal moving', body: 'Track progress, manage tasks, and close more deals.' },
];

const FAQS = [
  { q: 'Who is HomiHQ for?', a: 'HomiHQ is built for independent real estate agents, small teams, and growing brokerages who want a single organized system for leads, follow-ups, and daily tasks.' },
  { q: 'Does HomiHQ replace my CRM?', a: 'HomiHQ can serve as your primary lead and pipeline management tool. It\u2019s designed specifically for real estate workflows rather than generic CRM software.' },
  { q: 'How do follow-ups work?', a: 'HomiHQ tracks your lead interactions and surfaces timely reminders so no opportunity falls through the cracks. You can also set up automated outreach sequences.' },
  { q: 'Is my data secure?', a: 'Yes. All data is encrypted and stored securely. We follow industry best practices for data protection and privacy.' },
];

function Eyebrow({ children, style, ...rest }) {
  return (
    <p
      className="text-xs font-semibold uppercase"
      style={{ color: C.gold, letterSpacing: '0.22em', ...style }}
      {...rest}
    >
      {children}
    </p>
  );
}

function PrimaryBtn({ to, href, onClick, children, testId }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: '14px 28px', background: C.green, color: '#fff',
    fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase',
    fontWeight: 600, borderRadius: 2, transition: 'background 0.2s ease',
    border: `1px solid ${C.green}`, cursor: 'pointer',
  };
  const onMouseEnter = (e) => { e.currentTarget.style.background = C.greenDark; };
  const onMouseLeave = (e) => { e.currentTarget.style.background = C.green; };
  if (to) return <Link to={to} style={base} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid={testId}>{children}</Link>;
  if (href) return <a href={href} style={base} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid={testId}>{children}</a>;
  return <button type="button" onClick={onClick} style={base} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid={testId}>{children}</button>;
}

function GhostBtn({ to, href, children, testId }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: '13px 26px', background: 'transparent', color: C.green,
    fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase',
    fontWeight: 600, borderRadius: 2, transition: 'all 0.2s ease',
    border: `1px solid ${C.green}`,
  };
  const onMouseEnter = (e) => { e.currentTarget.style.background = C.green; e.currentTarget.style.color = '#fff'; };
  const onMouseLeave = (e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.green; };
  if (to) return <Link to={to} style={base} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid={testId}>{children}</Link>;
  return <a href={href} style={base} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid={testId}>{children}</a>;
}

function FaqRow({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderTop: idx === 0 ? `1px solid ${C.line}` : 'none',
        borderBottom: `1px solid ${C.line}`,
      }}
      data-testid={`faq-item-${idx}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-6 text-left"
        style={{ color: C.ink, background: 'transparent' }}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-serif" style={{ fontSize: '1.35rem', lineHeight: 1.35, color: C.green }}>
          {q}
        </span>
        <ChevronDown
          size={20}
          style={{
            color: C.gold, flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.25s ease',
          }}
        />
      </button>
      <div
        style={{
          maxHeight: open ? 300 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease, padding 0.3s ease',
          paddingBottom: open ? '1.5rem' : 0,
        }}
      >
        <p style={{ color: C.inkSoft, lineHeight: 1.75, maxWidth: 720 }}>{a}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div
      data-testid="landing-page"
      style={{
        background: C.bg,
        color: C.ink,
        minHeight: '100vh',
        fontFamily: "'Inter', -apple-system, sans-serif",
        scrollBehavior: 'smooth',
      }}
    >
      {/* NAV */}
      <header
        style={{
          position: 'sticky', top: 0, zIndex: 40,
          background: 'rgba(245, 240, 232, 0.92)',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        <div className="mx-auto flex items-center justify-between px-6 md:px-10 py-4" style={{ maxWidth: 1200 }}>
          <Link to="/" data-testid="nav-logo">
            <HomiLogo size={34} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map((it) => it.to ? (
              <Link
                key={it.label}
                to={it.to}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.ink, fontWeight: 500 }}
              >
                {it.label}
              </Link>
            ) : (
              <a
                key={it.label}
                href={it.href}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.ink, fontWeight: 500 }}
              >
                {it.label}
              </a>
            ))}
            <Link
              to="/login"
              data-testid="nav-login"
              style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.ink, fontWeight: 500 }}
            >
              Log In
            </Link>
            <PrimaryBtn to="/register" testId="nav-start-free">Start Your Free Trial</PrimaryBtn>
          </nav>

          {/* Mobile trigger */}
          <button
            type="button"
            className="md:hidden"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ color: C.green, padding: 6 }}
            data-testid="mobile-nav-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div
            className="md:hidden px-6 py-6"
            style={{ background: C.bgCard, borderTop: `1px solid ${C.line}` }}
            data-testid="mobile-nav-panel"
          >
            <div className="flex flex-col gap-5">
              {NAV_ITEMS.map((it) => it.to ? (
                <Link
                  key={it.label}
                  to={it.to}
                  onClick={() => setMobileOpen(false)}
                  style={{ fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.ink }}
                >
                  {it.label}
                </Link>
              ) : (
                <a
                  key={it.label}
                  href={it.href}
                  onClick={() => setMobileOpen(false)}
                  style={{ fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.ink }}
                >
                  {it.label}
                </a>
              ))}
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                style={{ fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.ink }}
              >
                Log In
              </Link>
              <PrimaryBtn to="/register" testId="mobile-start-free">Start Your Free Trial</PrimaryBtn>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="px-6 md:px-10 pt-10 md:pt-14 pb-16 md:pb-24">
        <div className="mx-auto text-center" style={{ maxWidth: 880 }}>
          <Eyebrow>Powered by Hired</Eyebrow>
          <h1
            className="font-serif mt-6"
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4.75rem)',
              lineHeight: 1.06,
              letterSpacing: '-0.01em',
              color: C.green,
              fontWeight: 500,
            }}
            data-testid="hero-headline"
          >
            Your Real Estate Business, Finally Under One Roof.
          </h1>
          <p
            className="mt-8 mx-auto"
            style={{ color: C.inkSoft, fontSize: '1.15rem', lineHeight: 1.7, maxWidth: 720 }}
          >
            HomiHQ organizes your leads, follows up automatically, manages calls and appointments,
            and keeps every opportunity moving.
          </p>

          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <PrimaryBtn to="/register" testId="hero-start-free">
              Start Your Free Trial <ArrowRight size={14} />
            </PrimaryBtn>
            <GhostBtn href="#how-it-works" testId="hero-see-how">See How It Works</GhostBtn>
          </div>

          <div className="mt-6">
            <Link
              to="/login"
              data-testid="hero-login-link"
              style={{ color: C.inkSoft, fontSize: 14, textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              Already have an account? Log in
            </Link>
          </div>

          <p
            className="mt-16"
            style={{
              color: C.inkSoft, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase',
              borderTop: `1px solid ${C.line}`, paddingTop: '2.5rem', maxWidth: 640, margin: '4rem auto 0',
            }}
            data-testid="trust-line"
          >
            Built for independent agents, growing teams, and busy real estate professionals.
          </p>

          {/* Dashboard preview */}
          <DashboardMockup />
        </div>
      </section>

      {/* PROBLEM / SOLUTION */}
      <section style={{ background: C.bgAlt, borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>
        <div className="mx-auto px-6 md:px-10 py-20 md:py-28 text-center" style={{ maxWidth: 900 }}>
          <h2
            className="font-serif"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', lineHeight: 1.15, color: C.green, fontWeight: 500 }}
          >
            More selling. Less chasing.
          </h2>
          <p className="mt-8" style={{ color: C.inkSoft, fontSize: '1.1rem', lineHeight: 1.75 }}>
            Leads, calls, appointments, follow-ups, and client details should not live across scattered
            apps and forgotten notes. HomiHQ brings the daily work of real estate into one organized
            command center.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="px-6 md:px-10 py-20 md:py-28">
        <div className="mx-auto" style={{ maxWidth: 1200 }}>
          <div className="text-center mb-16">
            <Eyebrow>Features</Eyebrow>
            <h2
              className="font-serif mt-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: C.green, fontWeight: 500, lineHeight: 1.2 }}
            >
              Everything you need. Nothing you don&apos;t.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="features-grid">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                style={{
                  background: C.bgCard,
                  border: `1px solid ${C.line}`,
                  borderRadius: 4,
                  padding: '2.25rem 2rem',
                  transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.borderColor = C.gold;
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(45, 80, 22, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = C.line;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  style={{
                    width: 46, height: 46, borderRadius: 2,
                    background: 'rgba(184,150,12,0.10)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '1.5rem',
                  }}
                >
                  <f.icon size={20} style={{ color: C.gold }} strokeWidth={1.6} />
                </div>
                <h3
                  style={{
                    fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: C.green, fontWeight: 700, marginBottom: '0.75rem',
                  }}
                >
                  {f.title}
                </h3>
                <p style={{ color: C.inkSoft, lineHeight: 1.65, fontSize: '0.95rem' }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        id="how-it-works"
        style={{ background: C.bgAlt, borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}
      >
        <div className="mx-auto px-6 md:px-10 py-20 md:py-28" style={{ maxWidth: 1100 }}>
          <div className="text-center mb-16">
            <Eyebrow>How It Works</Eyebrow>
            <h2
              className="font-serif mt-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: C.green, fontWeight: 500, lineHeight: 1.2 }}
              data-testid="how-headline"
            >
              From scattered to organized in three steps.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={s.n} data-testid={`step-${i}`} style={{ position: 'relative' }}>
                <div
                  className="font-serif"
                  style={{ fontSize: '3.5rem', color: C.gold, fontWeight: 400, lineHeight: 1 }}
                >
                  {s.n}
                </div>
                <div style={{ height: 1, background: C.line, margin: '1.5rem 0 1.25rem', width: 48 }} />
                <h3 className="font-serif" style={{ fontSize: '1.5rem', color: C.green, marginBottom: '0.75rem', lineHeight: 1.3 }}>
                  {s.title}
                </h3>
                <p style={{ color: C.inkSoft, lineHeight: 1.7 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIRED SECTION */}
      <section className="px-6 md:px-10 py-20 md:py-28">
        <div className="mx-auto" style={{ maxWidth: 900 }}>
          <div className="text-center">
            <Eyebrow>Powered by Hired</Eyebrow>
            <h2
              className="font-serif mt-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: C.green, fontWeight: 500, lineHeight: 1.2 }}
            >
              More than software. An operating system for your work.
            </h2>
            <p
              className="mt-8 mx-auto"
              style={{ color: C.inkSoft, fontSize: '1.05rem', lineHeight: 1.8, maxWidth: 720 }}
            >
              HomiHQ is built on HIRED — the intelligence platform that quietly powers the workflows,
              automations, and AI capabilities behind every screen. It is what turns HomiHQ from a set
              of tools into a working system that keeps your business moving.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING CTA */}
      <section
        style={{
          background: C.green,
          color: '#fff',
        }}
      >
        <div className="mx-auto px-6 md:px-10 py-20 md:py-24 text-center" style={{ maxWidth: 900 }}>
          <h2
            className="font-serif"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', fontWeight: 500, lineHeight: 1.2, color: '#fff' }}
          >
            Ready to put your business in motion?
          </h2>
          <p className="mt-6" style={{ color: 'rgba(255,255,255,0.82)', fontSize: '1.05rem', lineHeight: 1.7 }}>
            Choose the plan that fits your business.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/register"
              data-testid="cta-register"
              style={{
                background: C.gold, color: '#fff',
                padding: '14px 30px', fontSize: 12, letterSpacing: '0.18em',
                textTransform: 'uppercase', fontWeight: 600, borderRadius: 2,
                border: `1px solid ${C.gold}`,
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = C.goldSoft; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = C.gold; }}
            >
              Start Your Free Trial
            </Link>
            <Link
              to="/pricing"
              data-testid="cta-see-pricing"
              style={{
                background: 'transparent', color: '#fff',
                padding: '13px 28px', fontSize: 12, letterSpacing: '0.18em',
                textTransform: 'uppercase', fontWeight: 600, borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.5)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; }}
            >
              See Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 md:px-10 py-20 md:py-28">
        <div className="mx-auto" style={{ maxWidth: 880 }}>
          <div className="text-center mb-14">
            <Eyebrow>FAQ</Eyebrow>
            <h2
              className="font-serif mt-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: C.green, fontWeight: 500, lineHeight: 1.2 }}
            >
              Common questions.
            </h2>
          </div>
          <div>
            {FAQS.map((f, i) => <FaqRow key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        style={{ background: C.bgAlt, borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}
      >
        <div className="mx-auto px-6 md:px-10 py-24 md:py-32 text-center" style={{ maxWidth: 900 }}>
          <h2
            className="font-serif"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 3.75rem)', color: C.green, fontWeight: 500, lineHeight: 1.15 }}
          >
            Your next opportunity is already waiting.
          </h2>
          <p className="mt-8" style={{ color: C.inkSoft, fontSize: '1.1rem', lineHeight: 1.7 }}>
            Join real estate professionals who have put their business under control.
          </p>
          <div className="mt-12">
            <PrimaryBtn to="/register" testId="final-cta-register">
              Start Your Free Trial <ArrowRight size={14} />
            </PrimaryBtn>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: C.bg, borderTop: `1px solid ${C.line}` }}>
        <div
          className="mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-6"
          style={{ maxWidth: 1200 }}
        >
          <div className="flex flex-col items-center md:items-start gap-1">
            <HomiLogo size={28} />
            <p style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, marginTop: 6 }}>
              Powered by Hired
            </p>
          </div>

          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-3">
            <a href="#features" style={{ fontSize: 12, color: C.inkSoft, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Features</a>
            <a href="#how-it-works" style={{ fontSize: 12, color: C.inkSoft, letterSpacing: '0.1em', textTransform: 'uppercase' }}>How It Works</a>
            <Link to="/pricing" style={{ fontSize: 12, color: C.inkSoft, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Pricing</Link>
            <Link to="/login" style={{ fontSize: 12, color: C.inkSoft, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Log In</Link>
            <Link to="/register" style={{ fontSize: 12, color: C.inkSoft, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Register</Link>
          </nav>

          <div style={{ fontSize: 12, color: C.inkSoft, letterSpacing: '0.08em' }}>
            © {new Date().getFullYear()} HomiHQ · All rights reserved
          </div>
        </div>
      </footer>
    </div>
  );
}
